/**
 * Tabla canónica de administración con sort cliente + paginación + empty state.
 *
 * Reemplaza los `<table>` inline duplicados en los 15 componentes Admin*.tsx.
 * Características:
 *   - Header azul `#0E243E` (regla design system)
 *   - Hover fila `#F0F4FF`
 *   - Sort cliente por columna `sortable=true` (toggle asc/desc)
 *   - Paginación si `pagination` prop presente
 *   - Empty state si `rows.length === 0`
 *   - Columnas `mono=true` renderizan con JetBrains Mono (para códigos RF/RG/etc.)
 *
 * Si el backend ya pagina server-side: el shell no reordena los rows — respeta el
 * orden recibido y llama `pagination.onPageChange`. Si no hay paginación backend,
 * el shell hace sort in-memory sobre el array completo.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { ADMIN_TOKENS } from './adminTokens';
import { AdminSortIndicator, type SortDirection } from './AdminSortIndicator';
import { AdminPagination } from './AdminPagination';
import { AdminEmptyState } from './AdminEmptyState';
import { C } from '@/lib/design';

export interface AdminTableColumn<T> {
  key: string;
  label: string;
  width?: number | string;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  sortKey?: (row: T) => string | number | Date | null | undefined;
  render?: (row: T) => ReactNode;
  mono?: boolean;
}

export interface AdminTablePagination {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  labels?: {
    prev?: string;
    next?: string;
    pageOf?: (page: number, totalPages: number) => string;
    totalCount?: (total: number) => string;
  };
}

export interface AdminTableShellProps<T> {
  columns: AdminTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  actions?: (row: T) => ReactNode;
  actionsLabel?: string;
  emptyMessage?: string;
  emptyIcon?: ReactNode;
  loading?: boolean;
  loadingMessage?: string;
  pagination?: AdminTablePagination;
  defaultSort?: { key: string; direction: 'asc' | 'desc' };
  footer?: ReactNode;
}

function compareValues(a: unknown, b: unknown, direction: 'asc' | 'desc'): number {
  if (a == null && b == null) return 0;
  if (a == null) return direction === 'asc' ? -1 : 1;
  if (b == null) return direction === 'asc' ? 1 : -1;
  if (a instanceof Date && b instanceof Date) {
    return direction === 'asc' ? a.getTime() - b.getTime() : b.getTime() - a.getTime();
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return direction === 'asc' ? a - b : b - a;
  }
  const sa = String(a).toLocaleLowerCase();
  const sb = String(b).toLocaleLowerCase();
  const cmp = sa.localeCompare(sb, 'es');
  return direction === 'asc' ? cmp : -cmp;
}

export function AdminTableShell<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  actions,
  actionsLabel = '',
  emptyMessage = 'Sin resultados',
  emptyIcon,
  loading = false,
  loadingMessage = 'Cargando…',
  pagination,
  defaultSort,
  footer,
}: AdminTableShellProps<T>) {
  const [sortState, setSortState] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(
    defaultSort ?? null,
  );
  const [hoverRowKey, setHoverRowKey] = useState<string | null>(null);

  const sortedRows = useMemo(() => {
    if (!sortState) return rows;
    const col = columns.find((c) => c.key === sortState.key);
    if (!col || !col.sortable) return rows;
    const getValue = (row: T): unknown => {
      if (col.sortKey) return col.sortKey(row);
      return (row as Record<string, unknown>)[col.key];
    };
    return [...rows].sort((a, b) => compareValues(getValue(a), getValue(b), sortState.direction));
  }, [rows, sortState, columns]);

  const handleSort = (col: AdminTableColumn<T>) => {
    if (!col.sortable) return;
    setSortState((prev) => {
      if (!prev || prev.key !== col.key) return { key: col.key, direction: 'asc' };
      if (prev.direction === 'asc') return { key: col.key, direction: 'desc' };
      return null;
    });
  };

  const colSpan = columns.length + (actions ? 1 : 0);

  return (
    <div style={ADMIN_TOKENS.table}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 13,
        }}
      >
        <thead>
          <tr style={{ background: ADMIN_TOKENS.header.bg }}>
            {columns.map((col) => {
              const isSortedBy = sortState?.key === col.key;
              const direction: SortDirection = isSortedBy ? sortState.direction : null;
              return (
                <th
                  key={col.key}
                  onClick={() => handleSort(col)}
                  style={{
                    padding: ADMIN_TOKENS.header.padding,
                    textAlign: col.align ?? 'left',
                    color: ADMIN_TOKENS.header.color,
                    fontSize: ADMIN_TOKENS.header.fontSize,
                    fontWeight: ADMIN_TOKENS.header.fontWeight,
                    letterSpacing: ADMIN_TOKENS.header.letterSpacing,
                    cursor: col.sortable ? 'pointer' : 'default',
                    width: col.width,
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                  }}
                  aria-sort={
                    isSortedBy
                      ? sortState.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : col.sortable
                        ? 'none'
                        : undefined
                  }
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {col.label}
                    {col.sortable && <AdminSortIndicator direction={direction} />}
                  </span>
                </th>
              );
            })}
            {actions && (
              <th
                style={{
                  padding: ADMIN_TOKENS.header.padding,
                  width: 120,
                  color: ADMIN_TOKENS.header.color,
                  fontSize: ADMIN_TOKENS.header.fontSize,
                  fontWeight: ADMIN_TOKENS.header.fontWeight,
                  textAlign: 'right',
                }}
              >
                {actionsLabel}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td
                colSpan={colSpan}
                style={{
                  padding: '48px 16px',
                  textAlign: 'center',
                  color: C.textSec,
                  fontFamily: 'DM Sans, sans-serif',
                }}
              >
                {loadingMessage}
              </td>
            </tr>
          )}
          {!loading && sortedRows.length === 0 && (
            <tr>
              <td colSpan={colSpan} style={{ padding: 0 }}>
                <AdminEmptyState message={emptyMessage} icon={emptyIcon} />
              </td>
            </tr>
          )}
          {!loading &&
            sortedRows.map((row) => {
              const key = rowKey(row);
              const isHover = hoverRowKey === key;
              return (
                <tr
                  key={key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onMouseEnter={() => setHoverRowKey(key)}
                  onMouseLeave={() => setHoverRowKey(null)}
                  style={{
                    borderBottom: ADMIN_TOKENS.row.borderBottom,
                    background: isHover ? ADMIN_TOKENS.row.hover : 'transparent',
                    cursor: onRowClick ? 'pointer' : 'default',
                    transition: 'background 80ms linear',
                  }}
                >
                  {columns.map((col) => {
                    const value = col.render
                      ? col.render(row)
                      : (row as Record<string, unknown>)[col.key];
                    const rendered =
                      value == null || value === '' ? (
                        <span style={{ color: C.gray400 }}>—</span>
                      ) : (
                        (value as ReactNode)
                      );
                    return (
                      <td
                        key={col.key}
                        style={{
                          padding: ADMIN_TOKENS.row.padding,
                          fontSize: ADMIN_TOKENS.row.fontSize,
                          color: ADMIN_TOKENS.row.color,
                          textAlign: col.align ?? 'left',
                          ...(col.mono
                            ? {
                                fontFamily: ADMIN_TOKENS.code.fontFamily,
                                fontWeight: ADMIN_TOKENS.code.fontWeight,
                                fontSize: ADMIN_TOKENS.code.fontSize,
                                letterSpacing: ADMIN_TOKENS.code.letterSpacing,
                              }
                            : {}),
                        }}
                      >
                        {rendered}
                      </td>
                    );
                  })}
                  {actions && (
                    <td
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        padding: ADMIN_TOKENS.row.padding,
                        textAlign: 'right',
                      }}
                    >
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 4,
                        }}
                      >
                        {actions(row)}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
        </tbody>
      </table>
      {!loading && pagination && rows.length > 0 && <AdminPagination {...pagination} />}
      {!loading && !pagination && footer && <div style={ADMIN_TOKENS.pagination.bar}>{footer}</div>}
    </div>
  );
}
